/** Step-up state for bearer clients (M2). Tokens live in memory only: they are 45-minute,
 *  per-app proofs and must not survive an app restart. */
export type StepupApp = 'pazuru' | 'tsumi'

export const PAZURU_PREFIXES = [
  '/api/accounts', '/api/transactions', '/api/analytics', '/api/recurring',
  '/api/budgets', '/api/goals', '/api/forecasts', '/api/debts',
  '/api/chat', '/api/payslips',
  '/api/categorization', '/api/consents', '/api/data-quality', '/api/insights',
  '/api/mortgage-calculator', '/api/opportunities', '/api/reports', '/api/settings',
  '/api/subscriptions', '/api/sync',
  // Paths shared with Tsumi: Pazuru owns the bare prefix, Tsumi's sub-paths win below.
  '/api/tax/my-picture', '/api/export/payslips', '/api/export/transactions', '/api/import', '/api/lab',
]
export const TSUMI_PREFIXES = [
  '/api/etf', '/api/crypto', '/api/manual-assets', '/api/investment-connections',
  '/api/markets', '/api/portfolio', '/api/backtest', '/api/screener',
  '/api/comparator', '/api/simulations', '/api/sentiment', '/api/news',
  '/api/tax/italy', '/api/tax/france', '/api/tax/gains',
  '/api/export/excel', '/api/export/portfolio', '/api/import/account',
]

export const matches = (url: string, p: string) =>
  url === p || url.startsWith(p + '/') || url.startsWith(p + '?')

export function appForUrl(url: string): StepupApp | null {
  if (url.startsWith('/api/v1/pazuru/')) return 'pazuru'
  if (url.startsWith('/api/v1/tsumi/')) return 'tsumi'
  if (TSUMI_PREFIXES.some((p) => matches(url, p))) return 'tsumi'
  if (PAZURU_PREFIXES.some((p) => matches(url, p))) return 'pazuru'
  return null
}

const tokens: Record<StepupApp, string | null> = { pazuru: null, tsumi: null }
export const getStepupToken = (app: StepupApp) => tokens[app]
export function setStepupToken(app: StepupApp, token: string) { tokens[app] = token }
export function clearStepupTokens() {
  tokens.pazuru = null; tokens.tsumi = null
  declinedApp = null; emitDeclined()
}

let pendingApp: StepupApp | null = null
let waiters: ((ok: boolean) => void)[] = []
const listeners = new Set<(app: StepupApp | null) => void>()

export function subscribePending(cb: (app: StepupApp | null) => void): () => void {
  listeners.add(cb)
  cb(pendingApp)
  return () => { listeners.delete(cb) }
}

const emit = () => listeners.forEach((cb) => cb(pendingApp))

/** Ask the UI to run a step-up for `app`; resolves true when verified. One prompt at a time. */
export function requestStepup(app: StepupApp): Promise<boolean> {
  return new Promise((resolve) => {
    waiters.push(resolve)
    if (pendingApp === null) { pendingApp = app; emit() }
  })
}

/** App whose verification the user last dismissed ("Not now"): pages behind it show errors, and the
 *  banner uses this to explain why and offer a way back in. Cleared on success or sign-out. */
let declinedApp: StepupApp | null = null
const declinedListeners = new Set<(app: StepupApp | null) => void>()
const emitDeclined = () => declinedListeners.forEach((cb) => cb(declinedApp))

export function subscribeDeclined(cb: (app: StepupApp | null) => void): () => void {
  declinedListeners.add(cb)
  cb(declinedApp)
  return () => { declinedListeners.delete(cb) }
}

export function resolvePending(ok: boolean) {
  const done = waiters
  waiters = []
  const was = pendingApp
  pendingApp = null
  declinedApp = ok ? null : (was ?? declinedApp)
  emit()
  emitDeclined()
  done.forEach((w) => w(ok))
}
